import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Sparkles, RefreshCw, PenLine, ChevronRight, X } from 'lucide-react';
import { api, type OpenQuestion } from '../lib/api';
import { usePanelOpen } from '../lib/usePanelOpen';

/**
 * « Gnosis se demande… » (PRO-01) : la base met en avant ses propres trous sous
 * forme de questions, capturables en un clic. Repliable (soufflet) ; les
 * questions répondues ou écartées disparaissent. Invisible s'il n'y a rien.
 */
export function OpenQuestions({
  onAsk,
}: {
  onAsk: (
    subject: string,
    question: string,
    tag: string | undefined,
    resolve: { kind: 'question' | 'contradiction'; id: string },
  ) => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = usePanelOpen('gnosis.panel.questions.v2', false);
  const [questions, setQuestions] = useState<OpenQuestion[] | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    void api.proactive
      .questions()
      .then((r) => setQuestions(r.questions))
      .catch(() => setQuestions([]));
  }, []);

  const refresh = (e: React.MouseEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    setRefreshing(true);
    void api.proactive
      .refresh()
      .then((r) => setQuestions(r.questions))
      .catch(() => {})
      .finally(() => setRefreshing(false));
  };

  const dismiss = (id: string): void => {
    setQuestions((qs) => (qs ? qs.filter((q) => q.id !== id) : qs));
    void api.proactive.dismissQuestion(id).catch(() => {});
  };

  if (!questions || questions.length === 0) return null;

  return (
    <details
      open={open}
      onToggle={(e) => setOpen(e.currentTarget.open)}
      className="group rounded-2xl bg-gradient-to-br from-accent/10 to-surface-2 p-4 ring-1 ring-accent/20"
    >
      <summary className="flex cursor-pointer list-none items-center gap-2">
        <ChevronRight className="h-4 w-4 shrink-0 text-muted transition-transform group-open:rotate-90" />
        <h3 className="flex flex-1 items-center gap-1.5 text-sm font-semibold">
          <Sparkles className="h-4 w-4 text-accent" /> {t('proactive.title')}
        </h3>
        <button
          onClick={refresh}
          disabled={refreshing}
          title={t('proactive.refresh')}
          aria-label={t('proactive.refresh')}
          className="grid h-7 w-7 place-items-center rounded-lg text-muted hover:bg-surface hover:text-fg disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
        </button>
      </summary>
      <p className="mb-3 mt-1 text-xs text-muted">{t('proactive.subtitle')}</p>
      <ul className="space-y-1.5">
        {questions.map((q) => (
          <li key={q.id} className="group/q flex items-center gap-1">
            <button
              onClick={() => onAsk(q.subject, q.question, q.tag, { kind: 'question', id: q.id })}
              className="flex flex-1 items-center gap-2 rounded-xl bg-surface/70 px-3 py-2 text-left text-sm ring-1 ring-border transition hover:ring-accent"
            >
              <span className="flex-1">{q.question}</span>
              <span className="flex shrink-0 items-center gap-1 text-xs text-accent opacity-0 transition group-hover/q:opacity-100">
                <PenLine className="h-3.5 w-3.5" /> {t('proactive.answer')}
              </span>
            </button>
            <button
              onClick={() => dismiss(q.id)}
              title={t('proactive.dismiss')}
              aria-label={t('proactive.dismiss')}
              className="grid h-7 w-7 shrink-0 place-items-center rounded-lg text-muted opacity-0 transition hover:bg-surface hover:text-fg group-hover/q:opacity-100"
            >
              <X className="h-4 w-4" />
            </button>
          </li>
        ))}
      </ul>
    </details>
  );
}
